import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useMemo, useState, useEffect, createRef } from 'react';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import { injectIntl, IntlProvider, FormattedMessage, defineMessage } from 'react-intl';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { ActionButton, Text, ButtonGroup, Button, Tooltip, IllustratedMessage, Heading, Content, Link, MenuTrigger, Menu } from '@adobe/react-spectrum';
import AddCircle from '@spectrum-icons/workflow/AddCircle';
import Settings from '@spectrum-icons/workflow/Settings';
import cn from 'classnames';
import _extends from '@babel/runtime/helpers/esm/extends';
import GridLayout from 'react-grid-layout';
import { SizeMe } from 'react-sizeme';
import _toConsumableArray from '@babel/runtime/helpers/esm/toConsumableArray';
import NotFound from '@spectrum-icons/illustrations/NotFound';
import More from '@spectrum-icons/workflow/More';
import Label from '@react/react-spectrum/Label';
import { Stat } from '@quarry/stat';
import User from '@spectrum-icons/workflow/User';
import { TableView, TableHeader, Column, TableBody, Row, Cell } from '@react-spectrum/table';

function ownKeys$2(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$2(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$2(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$2(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

var NestedIntlProvider = function (_ref) {
  var intl = _ref.intl,
      locale = _ref.locale,
      messagesByLocale = _ref.messagesByLocale,
      children = _ref.children;
  // intl is null (not undefined) if a parent IntlProvider doesn't exist
  var parentLocale = intl && intl.locale;
  var parentMessages = intl && intl.messages;
  var actualLocale = locale || parentLocale || 'en-US';

  var getMessagesByLocale = function (actualLocale) {
    var messages = messagesByLocale[actualLocale];

    if (messages) {
      return messages;
    }

    var baseLocale = Object.keys(messagesByLocale).find(function (property) {
      return property.startsWith(actualLocale);
    }) || '';
    return messagesByLocale[baseLocale];
  }; // Only merge the messages when locale changes or messagesByLocale changes (shouldn't happen)
  // Do this because parentMessages could be really large and we don't want to run this on each render.


  var nestedMessages = useMemo(function () {
    return _objectSpread$2(_objectSpread$2({}, parentMessages || {}), getMessagesByLocale(actualLocale) || messagesByLocale['en-US']);
  }, [actualLocale, messagesByLocale]);
  return /*#__PURE__*/React.createElement(IntlProvider, {
    locale: actualLocale,
    messages: nestedMessages
  }, children);
};

var NestedIntlProvider$1 = injectIntl(NestedIntlProvider, {
  forwardRef: true,
  enforceContext: false
});

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z$6 = ".DashboardActions-module_actionContainer__1IFSI {\n  display: flex;\n  padding: 0 32px; }\n\n.DashboardActions-module_secondaryActions__1ifXx {\n  flex: 1; }\n  .DashboardActions-module_secondaryActions__1ifXx .DashboardActions-module_defaultControls__17fjz {\n    display: inline-flex;\n    margin-right: 10px; }\n\n.DashboardActions-module_primaryActions__1e1Aa {\n  white-space: nowrap; }\n  .DashboardActions-module_primaryActions__1e1Aa .DashboardActions-module_tooltip__2FJKf {\n    white-space: normal; }\n";
var styles$6 = {"actionContainer":"DashboardActions-module_actionContainer__1IFSI","secondaryActions":"DashboardActions-module_secondaryActions__1ifXx","defaultControls":"DashboardActions-module_defaultControls__17fjz","primaryActions":"DashboardActions-module_primaryActions__1e1Aa","tooltip":"DashboardActions-module_tooltip__2FJKf"};
styleInject(css_248z$6);

var DashboardActions = function (_ref) {
  var _ref$editMode = _ref.editMode,
      editMode = _ref$editMode === void 0 ? false : _ref$editMode,
      onAddWidget = _ref.onAddWidget,
      onCancel = _ref.onCancel,
      onSave = _ref.onSave,
      onDashboardEditableChange = _ref.onDashboardEditableChange,
      actions = _ref.actions,
      restrictModifyInfo = _ref.restrictModifyInfo,
      _ref$showActions = _ref.showActions,
      showActions = _ref$showActions === void 0 ? true : _ref$showActions,
      showAddWidget = _ref.showAddWidget,
      modifyButtonLabel = _ref.modifyButtonLabel,
      addWidgetButtonLabel = _ref.addWidgetButtonLabel,
      className = _ref.className;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      showManageTooltip = _useState2[0],
      setShowManageTooltip = _useState2[1];

  if (!showActions) {
    return null;
  }

  return /*#__PURE__*/React.createElement("div", {
    className: cn(styles$6.actionContainer, className)
  }, /*#__PURE__*/React.createElement("div", {
    className: styles$6.secondaryActions
  }, editMode && /*#__PURE__*/React.createElement(React.Fragment, null, showAddWidget && /*#__PURE__*/React.createElement("div", {
    className: styles$6.defaultControls
  }, /*#__PURE__*/React.createElement(ActionButton, {
    "data-test-id": "dashboard.add_widget",
    onPress: onAddWidget
  }, /*#__PURE__*/React.createElement(AddCircle, null), /*#__PURE__*/React.createElement(Text, null, addWidgetButtonLabel ? addWidgetButtonLabel : /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "@quarry/dashboard.add_widget",
    defaultMessage: "Add widget"
  })))), actions && actions())), /*#__PURE__*/React.createElement("div", {
    className: styles$6.primaryActions
  }, editMode ? /*#__PURE__*/React.createElement(ButtonGroup, null, /*#__PURE__*/React.createElement(Button, {
    "data-test-id": "dashboard.cancel",
    variant: "secondary",
    onPress: onCancel
  }, /*#__PURE__*/React.createElement(Text, null, /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "@quarry/dashboard.cancel",
    defaultMessage: "Cancel"
  }))), /*#__PURE__*/React.createElement(Button, {
    "data-test-id": "dashboard.save",
    variant: "cta",
    onPress: onSave
  }, /*#__PURE__*/React.createElement(Text, null, /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "@quarry/dashboard.save",
    defaultMessage: "Save"
  })))) : /*#__PURE__*/React.createElement("div", {
    onMouseOver: function onMouseOver() {
      return !!restrictModifyInfo && setShowManageTooltip(true);
    },
    onMouseOut: function onMouseOut() {
      return setShowManageTooltip(false);
    }
  }, /*#__PURE__*/React.createElement(Tooltip, {
    isOpen: showManageTooltip,
    placement: "left"
  }, /*#__PURE__*/React.createElement("div", {
    className: styles$6.tooltip
  }, restrictModifyInfo)), /*#__PURE__*/React.createElement(ActionButton, {
    "data-test-id": "dashboard.modify_dashboard",
    onPress: onDashboardEditableChange,
    isDisabled: Boolean(restrictModifyInfo)
  }, /*#__PURE__*/React.createElement(Settings, null), /*#__PURE__*/React.createElement(Text, null, modifyButtonLabel ? modifyButtonLabel : /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "@quarry/dashboard.modify_dashboard",
    defaultMessage: "Modify dashboard"
  }))))));
};

/**
 * @hidden
 * Sort top -> bottom, followed by left -> right
 */
var sortLayout = function (layout) {
  return _toConsumableArray(layout).sort(function layoutCompare(a, b) {
    if (a.y === b.y) {
      return a.x < b.x ? -1 : 1;
    }

    return a.y < b.y ? -1 : 1;
  });
};

var css_248z$5 = ".DashboardLayout-module_layout__YOl5f {\n  position: relative;\n  transition: height 200ms ease;\n  overflow: auto; }\n  .DashboardLayout-module_layout__YOl5f {\n    /* react-grid-layout specific classNames and style overrides */ }\n    .DashboardLayout-module_layout__YOl5f .react-resizable-handle {\n      position: absolute;\n      width: 20px;\n      height: 20px;\n      background-repeat: no-repeat;\n      background-origin: content-box;\n      box-sizing: border-box;\n      background-position: bottom right;\n      padding: 0 3px 3px 0; }\n    .DashboardLayout-module_layout__YOl5f .react-resizable-handle-se {\n      bottom: 0;\n      right: 0;\n      cursor: se-resize; }\n    .DashboardLayout-module_layout__YOl5f .react-grid-item {\n      position: relative;\n      background-color: white;\n      transition: all 500ms cubic-bezier(0.165, 0.84, 0.44, 1);\n      border: 1px solid #eaeaea;\n      border-radius: 4px; }\n      .DashboardLayout-module_layout__YOl5f .react-grid-item::after {\n        content: '';\n        position: absolute;\n        z-index: -1;\n        top: 0;\n        left: 0;\n        width: 100%;\n        height: 100%;\n        opacity: 0;\n        box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15), 0 4px 4px rgba(0, 0, 0, 0.15);\n        transition: opacity 500ms cubic-bezier(0.165, 0.84, 0.44, 1);\n        border-radius: 4px; }\n      .DashboardLayout-module_layout__YOl5f .react-grid-item.react-draggable {\n        border-color: #cacaca; }\n        .DashboardLayout-module_layout__YOl5f .react-grid-item.react-draggable .react-draggable-handle {\n          cursor: -webkit-grab;\n          cursor: grab; }\n        .DashboardLayout-module_layout__YOl5f .react-grid-item.react-draggable::after {\n          opacity: 1; }\n      .DashboardLayout-module_layout__YOl5f .react-grid-item.resizing {\n        z-index: 1;\n        will-change: width, height; }\n      .DashboardLayout-module_layout__YOl5f .react-grid-item.react-draggable-dragging {\n        transition: none;\n        z-index: 3;\n        will-change: transform; }\n      .DashboardLayout-module_layout__YOl5f .react-grid-item.dropping {\n        visibility: hidden; }\n      .DashboardLayout-module_layout__YOl5f .react-grid-item.react-grid-placeholder {\n        background: #cacaca;\n        border: 2px dashed #4b4b4b;\n        opacity: 0.2;\n        transition-duration: 100ms;\n        z-index: 2;\n        -webkit-user-select: none;\n           -moz-user-select: none;\n            -ms-user-select: none;\n                user-select: none; }\n      .DashboardLayout-module_layout__YOl5f .react-grid-item > .react-resizable-handle {\n        position: absolute;\n        width: 20px;\n        height: 20px;\n        bottom: 0;\n        right: 0;\n        cursor: se-resize;\n        transition: opacity 500ms cubic-bezier(0.165, 0.84, 0.44, 1);\n        opacity: 0; }\n        .DashboardLayout-module_layout__YOl5f .react-grid-item > .react-resizable-handle::after {\n          content: '';\n          position: absolute;\n          right: 8px;\n          bottom: 8px;\n          width: 14px;\n          height: 14px;\n          border-right: 1.5px solid rgba(0, 0, 0, 0.6);\n          border-bottom: 1.5px solid rgba(0, 0, 0, 0.6); }\n      .DashboardLayout-module_layout__YOl5f .react-grid-item.react-resizable-hide .react-resizable-handle {\n        opacity: 0;\n        cursor: default; }\n      .DashboardLayout-module_layout__YOl5f .react-grid-item:not(.react-resizable-hide) .react-resizable-handle {\n        opacity: 1; }\n";
var styles$5 = {"layout":"DashboardLayout-module_layout__YOl5f"};
styleInject(css_248z$5);

var _excluded$8 = ["children", "layout", "autosize", "compactType", "containerPadding", "draggableCancelSelector", "draggableHandleSelector", "numColumns", "rowHeight", "widgetMinWidth", "widgetMinHeight", "onLayoutChange"],
    _excluded2$2 = ["id", "minH", "minW"];

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var RESPONSIVE_BREAKPOINTS = {
  xxs: 768,
  xs: 1280,
  sm: 1768,
  md: 2160,
  lg: Number.POSITIVE_INFINITY
};
var RESPONSIVE_MARGINS = {
  xxs: [16, 16],
  xs: [24, 24],
  sm: [32, 32],
  md: [40, 40],
  lg: [48, 48]
};
/**
 * Handles the idiosyncracies of react-grid-layout and the normalization of layout props and widget props, so the
 * parent can be blissfully ignorant of them. This component intentionally locks down the number of options
 * offered in react-grid-layout to keep design consistent.
 */

var DashboardLayout = function (_ref) {
  var children = _ref.children,
      originalLayout = _ref.layout,
      _ref$autosize = _ref.autosize,
      autosize = _ref$autosize === void 0 ? true : _ref$autosize,
      _ref$compactType = _ref.compactType,
      compactType = _ref$compactType === void 0 ? 'vertical' : _ref$compactType,
      _ref$containerPadding = _ref.containerPadding,
      containerPadding = _ref$containerPadding === void 0 ? [32, 16] : _ref$containerPadding,
      draggableCancelSelector = _ref.draggableCancelSelector,
      draggableHandleSelector = _ref.draggableHandleSelector,
      _ref$numColumns = _ref.numColumns,
      numColumns = _ref$numColumns === void 0 ? 12 : _ref$numColumns,
      _ref$rowHeight = _ref.rowHeight,
      rowHeight = _ref$rowHeight === void 0 ? 40 : _ref$rowHeight,
      _ref$widgetMinWidth = _ref.widgetMinWidth,
      widgetMinWidth = _ref$widgetMinWidth === void 0 ? 2 : _ref$widgetMinWidth,
      _ref$widgetMinHeight = _ref.widgetMinHeight,
      widgetMinHeight = _ref$widgetMinHeight === void 0 ? 4 : _ref$widgetMinHeight,
      onLayoutChange = _ref.onLayoutChange,
      otherProps = _objectWithoutProperties(_ref, _excluded$8);

  // layout should be sorted in order for the layout order to match the dom for a11y
  var layout = sortLayout(originalLayout); // react-grid-layout must use `i` instead of `id` to designate items. This `i` value must match item `key` for each
  // child in children.

  var updatedLayout = layout.map(function (_ref2) {
    var id = _ref2.id,
        minH = _ref2.minH,
        minW = _ref2.minW,
        rest = _objectWithoutProperties(_ref2, _excluded2$2);

    return _objectSpread$1({
      i: id,
      minH: minH || widgetMinHeight,
      minW: minW || widgetMinWidth
    }, rest);
  });

  var handleLayoutChange = function (updatedLayout) {
    // Copy all layout positions to dashboard widgets
    var normalizedLayout = layout.map(function (widget) {
      var layoutItem = updatedLayout.find(function (layoutItem) {
        return layoutItem.i === widget.id;
      });

      if (layoutItem) {
        return _objectSpread$1(_objectSpread$1({}, widget), {}, {
          x: layoutItem.x,
          y: layoutItem.y,
          w: layoutItem.w,
          h: layoutItem.h
        });
      }

      return widget;
    });
    onLayoutChange(normalizedLayout);
  };

  var getResponsiveMargin = function (width) {
    if (!width) {
      return RESPONSIVE_MARGINS.xs;
    }

    if (width <= RESPONSIVE_BREAKPOINTS.xxs) {
      return RESPONSIVE_MARGINS.xxs;
    } else if (width <= RESPONSIVE_BREAKPOINTS.xs) {
      return RESPONSIVE_MARGINS.xs;
    } else if (width <= RESPONSIVE_BREAKPOINTS.sm) {
      return RESPONSIVE_MARGINS.sm;
    } else if (width <= RESPONSIVE_BREAKPOINTS.md) {
      return RESPONSIVE_MARGINS.md;
    }

    return RESPONSIVE_MARGINS.lg;
  };

  return /*#__PURE__*/React.createElement(SizeMe, null, function (_ref3) {
    var size = _ref3.size;
    return /*#__PURE__*/React.createElement(GridLayout, _extends({
      autoSize: autosize,
      className: styles$5.layout,
      cols: numColumns,
      rowHeight: rowHeight,
      containerPadding: containerPadding,
      layout: updatedLayout,
      margin: getResponsiveMargin(size.width),
      compactType: compactType,
      draggableCancel: draggableCancelSelector,
      draggableHandle: draggableHandleSelector,
      onLayoutChange: handleLayoutChange,
      width: size.width ? size.width : undefined
    }, otherProps), children);
  });
};

var DashboardEmptyState = function (_ref) {
  var showCTA = _ref.showCTA,
      onAddWidget = _ref.onAddWidget;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(IllustratedMessage, {
    marginTop: 60
  }, /*#__PURE__*/React.createElement(NotFound, {
    "aria-label": "No results"
  }), showCTA ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Heading, null, /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "@quarry/dashboard.empty_cta",
    defaultMessage: "Start by adding a widget"
  })), /*#__PURE__*/React.createElement(Content, null, /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "@quarry/dashboard.empty_text",
    defaultMessage: "Add widgets to this dashboard and position and size them."
  }), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement(Link, {
    onPress: onAddWidget
  }, /*#__PURE__*/React.createElement("a", null, /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "@quarry/dashboard.add_widget",
    defaultMessage: "Add widget"
  }))))) : /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "@quarry/dashboard.empty_nowidgetfound",
    defaultMessage: "No widgets found."
  })));
};

var css_248z$4 = ".Dashboard-module_dashboardContainer__2Ij-1 {\n  display: flex;\n  flex-direction: column;\n  flex: 1;\n  height: 100%; }\n";
var styles$4 = {"dashboardContainer":"Dashboard-module_dashboardContainer__2Ij-1"};
styleInject(css_248z$4);

var Dashboard = function (_ref) {
  var actions = _ref.actions,
      addWidgetButtonLabel = _ref.addWidgetButtonLabel,
      dashboardActionsClassName = _ref.dashboardActionsClassName,
      dashboardActionsComponent = _ref.dashboardActionsComponent,
      _ref$compactType = _ref.compactType,
      compactType = _ref$compactType === void 0 ? 'vertical' : _ref$compactType,
      draggableCancelSelector = _ref.draggableCancelSelector,
      draggableHandleSelector = _ref.draggableHandleSelector,
      _ref$editMode = _ref.editMode,
      editMode = _ref$editMode === void 0 ? false : _ref$editMode,
      _ref$isDraggable = _ref.isDraggable,
      isDraggable = _ref$isDraggable === void 0 ? true : _ref$isDraggable,
      _ref$isResizable = _ref.isResizable,
      isResizable = _ref$isResizable === void 0 ? true : _ref$isResizable,
      layout = _ref.layout,
      modifyButtonLabel = _ref.modifyButtonLabel,
      onAddWidget = _ref.onAddWidget,
      onCancel = _ref.onCancel,
      onDrag = _ref.onDrag,
      onDragStart = _ref.onDragStart,
      onDragStop = _ref.onDragStop,
      onLayoutChange = _ref.onLayoutChange,
      onResize = _ref.onResize,
      onResizeStart = _ref.onResizeStart,
      onResizeStop = _ref.onResizeStop,
      onSave = _ref.onSave,
      renderWidget = _ref.renderWidget,
      restrictModifyInfo = _ref.restrictModifyInfo,
      showActions = _ref.showActions,
      _ref$showAddWidget = _ref.showAddWidget,
      showAddWidget = _ref$showAddWidget === void 0 ? true : _ref$showAddWidget,
      _ref$widgetMinHeight = _ref.widgetMinHeight,
      widgetMinHeight = _ref$widgetMinHeight === void 0 ? 4 : _ref$widgetMinHeight,
      _ref$widgetMinWidth = _ref.widgetMinWidth,
      widgetMinWidth = _ref$widgetMinWidth === void 0 ? 2 : _ref$widgetMinWidth;

  var _useState = useState([]),
      _useState2 = _slicedToArray(_useState, 2),
      previousLayout = _useState2[0],
      setPreviousLayout = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      layoutDirty = _useState4[0],
      setLayoutDirty = _useState4[1];

  var _useState5 = useState(editMode),
      _useState6 = _slicedToArray(_useState5, 2),
      editing = _useState6[0],
      setEdit = _useState6[1];

  var dashboardRef = /*#__PURE__*/createRef();
  var hasWidgets = layout && layout.length > 0;
  useEffect(function () {
    // Manually adds a static class to all draggable selectors. This allows us to style the handle areas a bit
    // differently.
    var dashboardEl = dashboardRef && dashboardRef.current;

    if (dashboardEl && draggableHandleSelector) {
      var draggableAreas = dashboardEl.querySelectorAll(draggableHandleSelector);
      draggableAreas.forEach(function (draggableArea) {
        draggableArea.classList.add('react-draggable-handle');
      });
    }
  });

  var handleModifyDashboard = function () {
    setEdit(true);
    setPreviousLayout(layout);
  };

  var handleCancel = function () {
    setEdit(false);
    setLayoutDirty(false);

    if (layoutDirty) {
      // only reset layout if it changed
      onLayoutChange(previousLayout);
    }

    onCancel(previousLayout);
  };

  var handleSave = function () {
    setEdit(false);
    setLayoutDirty(false);
    onSave(layout);
  };

  return /*#__PURE__*/React.createElement("div", {
    ref: dashboardRef,
    className: styles$4.dashboardContainer
  }, function getActions() {
    if (dashboardActionsComponent) {
      return /*#__PURE__*/React.cloneElement(dashboardActionsComponent, {
        editMode: editing,
        onCancel: handleCancel,
        onSave: handleSave,
        onDashboardEditableChange: handleModifyDashboard
      });
    }

    return /*#__PURE__*/React.createElement(DashboardActions, {
      editMode: editing,
      onAddWidget: onAddWidget,
      onCancel: handleCancel,
      onSave: handleSave,
      onDashboardEditableChange: handleModifyDashboard,
      actions: actions,
      restrictModifyInfo: restrictModifyInfo,
      showActions: showActions,
      showAddWidget: showAddWidget,
      modifyButtonLabel: modifyButtonLabel,
      addWidgetButtonLabel: addWidgetButtonLabel,
      className: dashboardActionsClassName
    });
  }(), hasWidgets ? /*#__PURE__*/React.createElement(DashboardLayout, {
    layout: layout,
    compactType: compactType,
    draggableCancelSelector: draggableCancelSelector,
    draggableHandleSelector: draggableHandleSelector,
    isDraggable: isDraggable && editing,
    isResizable: isResizable && editing,
    widgetMinWidth: widgetMinWidth,
    widgetMinHeight: widgetMinHeight,
    onLayoutChange: function handleLayoutChange(layout) {
      setLayoutDirty(true);
      onLayoutChange(layout);
    },
    onDragStart: onDragStart,
    onDrag: onDrag,
    onDragStop: onDragStop,
    onResizeStart: onResizeStart,
    onResize: onResize,
    onResizeStop: onResizeStop
  }, layout.map(function (widget) {
    if (!widget.id) {
      throw new Error('layout item needs an id');
    }

    var widgetComponent = renderWidget(widget, {
      editing: editing
    }); // Keys are needed on children of DashboardLayout. Add the key to a fragment so consumer doesn't have to remember to do it.

    return /*#__PURE__*/React.createElement("div", {
      key: widget.id
    }, widgetComponent);
  })) : /*#__PURE__*/React.createElement(DashboardEmptyState, {
    showCTA: showAddWidget,
    onAddWidget: onAddWidget
  }));
};

var edit$3 = "Bearbeiten";
var localesDeDE = {
	"@quarry/dashboard.add_widget": "Widget hinzufügen",
	"@quarry/dashboard.cancel": "Abbrechen",
	"@quarry/dashboard.card.error_state.retry": "Versuchen Sie es erneut.",
	"@quarry/dashboard.empty_cta": "Beginnen Sie, indem Sie ein Widget hinzufügen",
	"@quarry/dashboard.empty_nowidgetfound": "Keine Widgets gefunden.",
	"@quarry/dashboard.empty_text": "Fügen Sie Widgets zu diesem Dashboard hinzu und passen Sie ihre Position und Größe an.",
	"@quarry/dashboard.modify_dashboard": "Dashboard ändern",
	"@quarry/dashboard.save": "Speichern",
	edit: edit$3,
	"no.resize": "Statisches Widget, Größenänderung und Verschieben nicht möglich"
};

var edit$2 = "Edit";
var localesEnUs = {
	"@quarry/dashboard.add_widget": "Add widget",
	"@quarry/dashboard.cancel": "Cancel",
	"@quarry/dashboard.card.error_state.retry": "Try again",
	"@quarry/dashboard.empty_cta": "Start by adding a widget",
	"@quarry/dashboard.empty_nowidgetfound": "No widgets found.",
	"@quarry/dashboard.empty_text": "Add widgets to this dashboard and position and size them.",
	"@quarry/dashboard.modify_dashboard": "Modify dashboard",
	"@quarry/dashboard.save": "Save",
	edit: edit$2,
	"no.resize": "Static widget, cannot resize or move"
};

var edit$1 = "Modifier";
var localesFrFr = {
	"@quarry/dashboard.add_widget": "Ajouter le widget",
	"@quarry/dashboard.cancel": "Annuler",
	"@quarry/dashboard.card.error_state.retry": "Réessayer",
	"@quarry/dashboard.empty_cta": "Commencer par ajouter un widget",
	"@quarry/dashboard.empty_nowidgetfound": "Aucun widget trouvé.",
	"@quarry/dashboard.empty_text": "Ajoutez des widgets à ce tableau de bord et définissez une position et une dimension.",
	"@quarry/dashboard.modify_dashboard": "Modifier le tableau de bord",
	"@quarry/dashboard.save": "Enregistrer",
	edit: edit$1,
	"no.resize": "Widget statique, redimensionnement ou déplacement impossible"
};

var edit = "編集";
var localesJaJp = {
	"@quarry/dashboard.add_widget": "ウィジェットを追加",
	"@quarry/dashboard.cancel": "キャンセル",
	"@quarry/dashboard.card.error_state.retry": "もう一度試す",
	"@quarry/dashboard.empty_cta": "最初にウィジェットを追加",
	"@quarry/dashboard.empty_nowidgetfound": "ウィジェットが見つかりません。",
	"@quarry/dashboard.empty_text": "このダッシュボードにウィジェットを追加して、位置とサイズを設定します。",
	"@quarry/dashboard.modify_dashboard": "ダッシュボードを変更",
	"@quarry/dashboard.save": "保存",
	edit: edit,
	"no.resize": "静的ウィジェットです。サイズ変更や移動はできません"
};

var _excluded$7 = ["locale"];
var messagesByLocale = {
  'de-DE': localesDeDE,
  'en-US': localesEnUs,
  'fr-FR': localesFrFr,
  'ja-JP': localesJaJp
};

var DashboardContainer = function (_ref) {
  var locale = _ref.locale,
      otherProps = _objectWithoutProperties(_ref, _excluded$7);

  return /*#__PURE__*/React.createElement(NestedIntlProvider$1, {
    locale: locale,
    messagesByLocale: messagesByLocale
  }, /*#__PURE__*/React.createElement(Dashboard, otherProps));
};

var css_248z$3 = ".DashboardWidget-module_widgetContainer__3f8zf {\n  position: relative;\n  display: flex;\n  flex-direction: column;\n  height: 100%;\n  background-color: white;\n  border-radius: 4px; }\n\n.DashboardWidget-module_widgetHeader__1bOxA {\n  display: flex;\n  padding: 8px 16px 8px 16px; }\n\n.DashboardWidget-module_widgetTitle__3L73D {\n  display: inline-block;\n  flex: 1;\n  line-height: 32px;\n  margin: 0;\n  margin-right: 8px;\n  white-space: nowrap;\n  overflow-x: hidden;\n  text-overflow: ellipsis; }\n\n.DashboardWidget-module_widgetContent__2FDeX {\n  position: relative;\n  display: flex;\n  flex: 1;\n  overflow: auto;\n  padding: 16px;\n  padding-top: 0; }\n";
var styles$3 = {"widgetContainer":"DashboardWidget-module_widgetContainer__3f8zf","widgetHeader":"DashboardWidget-module_widgetHeader__1bOxA","widgetTitle":"DashboardWidget-module_widgetTitle__3L73D","widgetContent":"DashboardWidget-module_widgetContent__2FDeX"};
styleInject(css_248z$3);

var _excluded$6 = ["children", "className", "headerClassName", "intl", "menuItems", "title", "headerContent", "headerContentClass", "onMenuItemSelect"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var moreOptions = defineMessage({
  id: '@quarry/dashboard.more_options',
  defaultMessage: 'More options'
});

var DashboardWidget = function (_ref) {
  var children = _ref.children,
      className = _ref.className,
      headerClassName = _ref.headerClassName,
      intl = _ref.intl,
      menuItems = _ref.menuItems,
      title = _ref.title,
      headerContent = _ref.headerContent,
      headerContentClass = _ref.headerContentClass,
      onMenuItemSelect = _ref.onMenuItemSelect,
      otherProps = _objectWithoutProperties(_ref, _excluded$6);

  return /*#__PURE__*/React.createElement("div", _extends({
    key: title,
    className: cn(styles$3.widgetContainer, className)
  }, otherProps), /*#__PURE__*/React.createElement("div", {
    className: cn(styles$3.widgetHeader, headerClassName)
  }, title && /*#__PURE__*/React.createElement("h2", {
    title: title,
    className: cn(styles$3.widgetTitle, 'spectrum-Heading4')
  }, title), headerContent && /*#__PURE__*/React.createElement("span", {
    className: headerContentClass
  }, headerContent), menuItems && menuItems.length > 0 && /*#__PURE__*/React.createElement(MenuTrigger, {
    align: "end"
  }, /*#__PURE__*/React.createElement(ActionButton, {
    "aria-label": intl.formatMessage(_objectSpread({}, moreOptions)),
    isQuiet: true
  }, /*#__PURE__*/React.createElement(More, null)), /*#__PURE__*/React.createElement(Menu, {
    onAction: onMenuItemSelect
  }, menuItems))), /*#__PURE__*/React.createElement("div", {
    className: styles$3.widgetContent
  }, children));
};

var DashboardWidget$1 = injectIntl(DashboardWidget);

var css_248z$2 = ".DashboardCard-module_errorContentContainer__3CtSR {\n  color: #b3b3b3;\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n  align-items: center;\n  margin-top: 0.25rem;\n  font-size: 0.75rem; }\n\n.DashboardCard-module_message__3Wo0K {\n  font-style: italic; }\n";
var styles$2 = {"errorContentContainer":"DashboardCard-module_errorContentContainer__3CtSR","message":"DashboardCard-module_message__3Wo0K"};
styleInject(css_248z$2);

var _excluded$5 = ["icon", "message", "onRetry"];

var DashboardCardErrorState = function (_ref) {
  var icon = _ref.icon,
      message = _ref.message,
      onRetry = _ref.onRetry,
      otherProps = _objectWithoutProperties(_ref, _excluded$5);

  return /*#__PURE__*/React.createElement("div", _extends({
    className: styles$2.errorContentContainer
  }, otherProps), icon, /*#__PURE__*/React.createElement("div", {
    className: styles$2.message
  }, message), onRetry && /*#__PURE__*/React.createElement(Button, {
    onPress: onRetry,
    variant: "primary"
  }, /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "@quarry/dashboard.card.error_state.retry",
    defaultMessage: "Try again"
  })));
};

var css_248z$1 = ".DashboardHeadline-module_DashboardHeadline__25aN9 {\n  display: flex;\n  flex-wrap: wrap;\n  width: 100%;\n  min-height: 13.125rem;\n  height: auto;\n  background-color: white; }\n\n.DashboardHeadline-module_description__3_poR {\n  margin: 0;\n  font-weight: 300;\n  text-transform: none; }\n\n.DashboardHeadline-module_card__QztLh {\n  display: flex;\n  flex-direction: column;\n  height: 100%;\n  transition: all 130ms ease-in-out; }\n\n.DashboardHeadline-module_cardContainer__r8PkQ {\n  border: none;\n  box-sizing: border-box;\n  height: 100%;\n  overflow: hidden;\n  position: relative;\n  transition: all 130ms ease-in-out;\n  padding: 0 2rem 2rem;\n  flex: 0 1 25%; }\n\n.DashboardHeadline-module_cardContent__1M3VQ {\n  font-weight: 300;\n  line-height: 1.25rem;\n  padding: 1.5rem 0 0 0;\n  margin-bottom: 1rem;\n  flex: 0; }\n  .DashboardHeadline-module_cardContent__1M3VQ:first-child {\n    padding-top: 1.5rem; }\n  .DashboardHeadline-module_cardContent__1M3VQ:last-child {\n    padding-bottom: 1.5rem; }\n\n.DashboardHeadline-module_cardHeader__2ApAe {\n  box-sizing: border-box;\n  display: flex;\n  flex-flow: row nowrap;\n  flex: none;\n  min-height: 3.5625rem;\n  padding: 1.5rem 0 1.5rem 0;\n  align-items: flex-start;\n  border-bottom: 0.0625rem solid #eaeaea; }\n\n.DashboardHeadline-module_cardHeaderBorder__y7pvW + .DashboardHeadline-module_cardContent__1M3VQ {\n  padding-top: 1.5rem; }\n";
var styles$1 = {"DashboardHeadline":"DashboardHeadline-module_DashboardHeadline__25aN9","description":"DashboardHeadline-module_description__3_poR","card":"DashboardHeadline-module_card__QztLh","cardContainer":"DashboardHeadline-module_cardContainer__r8PkQ","cardContent":"DashboardHeadline-module_cardContent__1M3VQ","cardHeader":"DashboardHeadline-module_cardHeader__2ApAe","cardHeaderBorder":"DashboardHeadline-module_cardHeaderBorder__y7pvW"};
styleInject(css_248z$1);

var _excluded$4 = ["border", "children", "className"];

var DashboardCardHeader = function (_ref) {
  var _ref$border = _ref.border,
      border = _ref$border === void 0 ? false : _ref$border,
      children = _ref.children,
      className = _ref.className,
      otherProps = _objectWithoutProperties(_ref, _excluded$4);

  return /*#__PURE__*/React.createElement("header", _extends({
    className: cn(className, styles$1.cardHeader, _defineProperty({}, styles$1.cardHeaderBorder, border))
  }, otherProps), children);
};

var _excluded$3 = ["className", "children"];

var DashboardCardContent = function (_ref) {
  var className = _ref.className,
      children = _ref.children,
      otherProps = _objectWithoutProperties(_ref, _excluded$3);

  return /*#__PURE__*/React.createElement("div", _extends({
    className: cn(styles$1.cardContent, className)
  }, otherProps), children);
};

var _excluded$2 = ["title", "description", "hasError", "errorState", "children", "className", "border"],
    _excluded2$1 = ["children"];

var HeadlineCard = function (_ref) {
  var title = _ref.title,
      description = _ref.description,
      hasError = _ref.hasError,
      errorState = _ref.errorState,
      children = _ref.children,
      className = _ref.className,
      border = _ref.border,
      otherProps = _objectWithoutProperties(_ref, _excluded$2);

  return /*#__PURE__*/React.createElement("div", _extends({
    className: cn(styles$1.cardContainer, className),
    role: "gridcell"
  }, otherProps), /*#__PURE__*/React.createElement("article", {
    className: styles$1.card
  }, function content() {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DashboardCardHeader, {
      className: styles$1.header,
      border: border,
      "data-test-id": "headline.card-header"
    }, /*#__PURE__*/React.createElement(Heading, {
      level: 4,
      margin: 0
    }, title)), /*#__PURE__*/React.createElement(DashboardCardContent, {
      className: styles$1.content,
      "data-test-id": "headline.card-content"
    }, hasError ? errorState() : children), description && !hasError && /*#__PURE__*/React.createElement("p", {
      className: cn('spectrum-Body5', styles$1.description),
      "data-test-id": "headline.card-description"
    }, description));
  }()));
};

HeadlineCard.defaultProps = {
  title: null,
  description: null,
  className: undefined,
  children: null,
  border: false
};

var DashboardHeadline = function (_ref2) {
  var children = _ref2.children,
      otherProps = _objectWithoutProperties(_ref2, _excluded2$1);

  return /*#__PURE__*/React.createElement("div", _extends({
    className: styles$1.DashboardHeadline
  }, otherProps), children);
};

DashboardHeadline.defaultProps = {
  chiildren: null
};

var css_248z = ".DashboardSummary-module_dashboardSummaryMetric__3qd4u {\n  display: flex;\n  flex: 1;\n  flex-direction: column;\n  height: 100%;\n  justify-content: center;\n  text-align: center; }\n\n.DashboardSummary-module_userIcon__1ESOk {\n  color: #707070; }\n\n.DashboardSummary-module_category__1YIns {\n  font-size: 18px;\n  font-weight: 700; }\n\n.DashboardSummary-module_deltaLabelContainer__3melV {\n  padding-top: 16px; }\n";
var styles = {"dashboardSummaryMetric":"DashboardSummary-module_dashboardSummaryMetric__3qd4u","userIcon":"DashboardSummary-module_userIcon__1ESOk","category":"DashboardSummary-module_category__1YIns","deltaLabelContainer":"DashboardSummary-module_deltaLabelContainer__3melV"};
styleInject(css_248z);

var _excluded$1 = ["className", "category", "deltaNumber", "totalNumber", "icon"];

var DashboardSummary = function (_ref) {
  var className = _ref.className,
      category = _ref.category,
      deltaNumber = _ref.deltaNumber,
      totalNumber = _ref.totalNumber,
      icon = _ref.icon,
      otherProps = _objectWithoutProperties(_ref, _excluded$1);

  return /*#__PURE__*/React.createElement("div", _extends({
    className: cn(styles.dashboardSummaryMetric, className)
  }, otherProps), /*#__PURE__*/React.createElement("div", {
    className: styles.userIcon
  }, icon && /*#__PURE__*/React.cloneElement(icon, {
    size: 'XL'
  })), /*#__PURE__*/React.createElement(Stat, {
    size: "xl",
    value: totalNumber
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.category
  }, category), deltaNumber && /*#__PURE__*/React.createElement("div", {
    className: styles.deltaLabelContainer
  }, /*#__PURE__*/React.createElement(Label, null, deltaNumber)));
};

DashboardSummary.defaultProps = {
  icon: /*#__PURE__*/React.createElement(User, null)
};

var _excluded = ["field", "key", "title", "headerRenderer"],
    _excluded2 = ["className", "columns", "rows", "tableProps"];

var tableColumns = function (columns) {
  return columns.map(function (column) {
    var field = column.field,
        key = column.key,
        title = column.title,
        headerRenderer = column.headerRenderer,
        restColumnProps = _objectWithoutProperties(column, _excluded);

    return /*#__PURE__*/React.createElement(Column, _extends({
      key: key || field || title
    }, restColumnProps), headerRenderer ? headerRenderer(column) : title);
  });
};

var tableRowCells = function (row, rowIdx, columns) {
  return columns.map(function (_ref, idx) {
    var field = _ref.field,
        cellRenderer = _ref.cellRenderer;
    return /*#__PURE__*/React.createElement(Cell, {
      key: "cell_".concat(rowIdx, "_").concat(idx)
    }, cellRenderer ? cellRenderer(row) : row[field]);
  });
};

var tableRows = function (rows, columns) {
  return rows.map(function (row, idx) {
    return /*#__PURE__*/React.createElement(Row, {
      key: "row_".concat(idx)
    }, tableRowCells(row, idx, columns));
  });
};

var DashboardTable = function (_ref2) {
  var className = _ref2.className,
      columns = _ref2.columns,
      rows = _ref2.rows,
      tableProps = _ref2.tableProps,
      otherProps = _objectWithoutProperties(_ref2, _excluded2);

  return /*#__PURE__*/React.createElement("div", _extends({
    className: cn(className)
  }, otherProps), /*#__PURE__*/React.createElement(TableView, tableProps, /*#__PURE__*/React.createElement(TableHeader, null, tableColumns(columns)), /*#__PURE__*/React.createElement(TableBody, null, tableRows(rows, columns))));
};

export { DashboardContainer as Dashboard, DashboardCardErrorState, DashboardHeadline, DashboardSummary, DashboardTable, DashboardWidget$1 as DashboardWidget, HeadlineCard };
